<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useNotificationStore } from '@/stores/NotificationStore.js';
import FormChartVisualization from '@/components/forms/charts/FormChartVisualization.vue';
import FormChartDataTable from '@/components/forms/charts/FormChartDataTable.vue';

const props = defineProps({
    initialFormId: {
        type: Number,
        required: true,
    },
});

const notificationStore = useNotificationStore();

const view = ref('list'); // 'list' | 'configure'

const contextLoaded = ref(false);

const chartableFields = ref([]);
const comparisonForms = ref([]);
const charts = ref([]);

const creating = ref(false);
const deletingChartId = ref(null);
const editingChartId = ref(null);
const updatingChartId = ref(null);
const previewingChartId = ref(null);
const previewLoadingChartId = ref(null);
const previewErrors = ref({});
const previewDataByChartId = ref({});

const formDraft = reactive({
    field_uuids: [],
    chart_type: 'bar',
    name: '',
    comparison_settings: {
        enabled: false,
        form_id: null,
        match: 'field_title',
    },
});

const editDraft = reactive({
    field_uuids: [],
    chart_type: 'bar',
    name: '',
    comparison_settings: {
        enabled: false,
        form_id: null,
        match: 'field_title',
    },
});

const chartTypeOptions = [
    { label: _mft('form:chart.type.bar'), value: 'bar' },
    { label: _mft('form:chart.type.line'), value: 'line' },
    { label: _mft('form:chart.type.donut'), value: 'donut' },
    { label: _mft('form:chart.type.table'), value: 'table' },
];

const comparisonChartTypes = ['bar', 'donut'];

const supportsComparison = (chartType) => comparisonChartTypes.includes(chartType);

const isEditingMode = computed(() => editingChartId.value !== null);

const fieldOptions = computed(() => {
    return chartableFields.value.map((field) => ({
        label: field.title,
        value: field.field_uuid,
    }));
});

const comparisonFormOptions = computed(() => {
    return comparisonForms.value
        .filter((form) => Number(form.id) !== Number(props.initialFormId))
        .map((form) => ({
            label: form.title,
            value: Number(form.id),
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
});

const canCreateChart = computed(() => {
    return (
        formDraft.name.length > 0 &&
        formDraft.field_uuids.length > 0 &&
        formDraft.chart_type !== '' &&
        comparisonSettingsAreValid(formDraft)
    );
});

const canSaveEditedChart = computed(() => {
    return (
        editingChartId.value !== null &&
        editDraft.field_uuids.length > 0 &&
        editDraft.chart_type !== '' &&
        comparisonSettingsAreValid(editDraft)
    );
});

const chartTypeLabel = (chartType) => {
    return chartTypeOptions.find((option) => option.value === chartType)?.label || chartType;
};

const chartIdMatches = (chart, id) => {
    return Number(chart?.id) === Number(id);
};

const resetEditDraft = () => {
    editingChartId.value = null;
    editDraft.field_uuids = [];
    editDraft.chart_type = 'bar';
    editDraft.name = '';
    resetComparisonSettings(editDraft);
};

const resetPreviewState = () => {
    previewingChartId.value = null;
    previewLoadingChartId.value = null;
    previewErrors.value = {};
    previewDataByChartId.value = {};
};

const availableFieldUuids = computed(() => {
    return chartableFields.value.map((field) => field.field_uuid);
});

const normalizeFieldUuids = (fieldUuids) => {
    const available = availableFieldUuids.value;
    return (fieldUuids || [])
        .filter((fieldUuid) => typeof fieldUuid === 'string' && available.includes(fieldUuid))
        .filter((fieldUuid, index, arr) => arr.indexOf(fieldUuid) === index);
};

const resetComparisonSettings = (draft) => {
    draft.comparison_settings = {
        enabled: false,
        form_id: null,
        match: 'field_title',
    };
};

const normalizeComparisonSettings = (settings) => {
    if (!settings || typeof settings !== 'object' || settings.enabled !== true) {
        return {
            enabled: false,
            form_id: null,
            match: 'field_title',
        };
    }

    const formId = Number(settings.form_id ?? settings.formId);

    return {
        enabled: true,
        form_id: Number.isInteger(formId) && formId > 0 ? formId : null,
        match: 'field_title',
    };
};

const comparisonSettingsAreValid = (draft) => {
    if (!supportsComparison(draft.chart_type) || draft.comparison_settings.enabled !== true) {
        return true;
    }

    return Number.isInteger(Number(draft.comparison_settings.form_id)) &&
        Number(draft.comparison_settings.form_id) > 0;
};

const comparisonSettingsPayload = (draft) => {
    if (!supportsComparison(draft.chart_type) || draft.comparison_settings.enabled !== true) {
        return null;
    }

    return {
        version: 1,
        enabled: true,
        form_id: Number(draft.comparison_settings.form_id),
        match: 'field_title',
    };
};

const loadChartContext = async () => {
    contextLoaded.value = false;

    const [fieldsRes, chartsRes, formsRes] = await Promise.all([
        axios.get(route('api.form.chartable-fields', { id: props.initialFormId })),
        axios.get(route('api.form.chart.index', { id: props.initialFormId })),
        axios.get(route('api.form.index'), { params: { only_chartable: true } }),
    ]);

    chartableFields.value = fieldsRes.data.chartable_fields || [];
    charts.value = chartsRes.data.charts || [];
    comparisonForms.value = formsRes.data.forms || [];

    formDraft.field_uuids = normalizeFieldUuids(formDraft.field_uuids);
    if (formDraft.field_uuids.length === 0 && availableFieldUuids.value.length > 0) {
        formDraft.field_uuids = [availableFieldUuids.value[0]];
    }

    if (editingChartId.value !== null) {
        const editingChart = charts.value.find((chart) => chartIdMatches(chart, editingChartId.value));
        if (!editingChart) {
            resetEditDraft();
        } else {
            editDraft.field_uuids = normalizeFieldUuids(editDraft.field_uuids);
            if (editDraft.field_uuids.length === 0 && availableFieldUuids.value.length > 0) {
                editDraft.field_uuids = [availableFieldUuids.value[0]];
            }
        }
    }

    if (previewingChartId.value !== null) {
        const previewingChart = charts.value.find((chart) => chartIdMatches(chart, previewingChartId.value));
        if (!previewingChart) {
            previewingChartId.value = null;
        }
    }

    contextLoaded.value = true;
};

const createChart = async () => {
    if (!canCreateChart.value || creating.value) {
        return;
    }

    creating.value = true;

    try {
        await axios.post(route('api.form.chart.store', { id: props.initialFormId }), {
            field_uuids: formDraft.field_uuids,
            chart_type: formDraft.chart_type,
            name: formDraft.name || null,
            comparison_settings: comparisonSettingsPayload(formDraft),
        });

        await loadChartContext();

        notificationStore.addNotification({
            type: 'success',
            title: _mft('form:chart.notification.created.title'),
            message: _mft('form:chart.notification.created.message'),
            dismissAfter: 3000,
        });

        view.value = 'list';
    } catch (error) {
        notificationStore.addNotification({
            type: 'error',
            title: _mft('form:chart.notification.createError.title'),
            message: _mft('form:chart.notification.createError.message'),
            dismissAfter: 5000,
        });
    } finally {
        creating.value = false;
    }
};

const startNewChart = () => {
    resetEditDraft();
    formDraft.field_uuids = availableFieldUuids.value.length > 0 ? [availableFieldUuids.value[0]] : [];
    formDraft.chart_type = 'bar';
    formDraft.name = '';
    resetComparisonSettings(formDraft);
    view.value = 'configure';
};

const startEditChart = (chart) => {
    if (updatingChartId.value !== null || deletingChartId.value !== null) {
        return;
    }

    if (isPreviewingChart(chart)) {
        closePreview();
    }

    editingChartId.value = Number(chart.id);
    editDraft.field_uuids = normalizeFieldUuids(chart.field_uuids);
    if (editDraft.field_uuids.length === 0 && availableFieldUuids.value.length > 0) {
        editDraft.field_uuids = [availableFieldUuids.value[0]];
    }
    editDraft.chart_type = chart.chart_type || 'bar';
    editDraft.name = chart.name || '';
    editDraft.comparison_settings = normalizeComparisonSettings(chart.comparison_settings);
    view.value = 'configure';
};

const cancelConfigure = () => {
    if (updatingChartId.value !== null) {
        return;
    }
    resetEditDraft();
    view.value = 'list';
};

const saveEditedChart = async () => {
    if (editingChartId.value === null || updatingChartId.value !== null || !canSaveEditedChart.value) {
        return;
    }

    updatingChartId.value = editingChartId.value;

    try {
        await axios.put(route('api.form.chart.update', {
            id: props.initialFormId,
            chartId: editingChartId.value,
        }), {
            field_uuids: editDraft.field_uuids,
            chart_type: editDraft.chart_type,
            name: editDraft.name || null,
            comparison_settings: comparisonSettingsPayload(editDraft),
        });

        await loadChartContext();
        resetEditDraft();

        notificationStore.addNotification({
            type: 'success',
            title: _mft('form:chart.notification.updated.title'),
            message: _mft('form:chart.notification.updated.message'),
            dismissAfter: 3000,
        });

        view.value = 'list';
    } catch (error) {
        notificationStore.addNotification({
            type: 'error',
            title: _mft('form:chart.notification.updateError.title'),
            message: error.response.data.message,
        });
    } finally {
        updatingChartId.value = null;
    }
};

const deleteChart = async (chart) => {
    if (deletingChartId.value !== null) {
        return;
    }

    if (!window.confirm(_mft('form:chart.deleteConfirm'))) {
        return;
    }

    deletingChartId.value = chart.id;

    try {
        await axios.delete(route('api.form.chart.destroy', {
            id: props.initialFormId,
            chartId: chart.id,
        }));

        await loadChartContext();
    } catch (error) {
        notificationStore.addNotification({
            type: 'error',
            title: _mft('form:chart.notification.deleteError.title'),
            message: _mft('form:chart.notification.deleteError.message'),
            dismissAfter: 5000,
        });
    } finally {
        deletingChartId.value = null;
    }
};

const closePreview = () => {
    previewingChartId.value = null;
};

const isPreviewingChart = (chart) => {
    return chartIdMatches(chart, previewingChartId.value);
};

const previewErrorFor = (chart) => {
    return previewErrors.value[String(chart.id)] || null;
};

const previewDataFor = (chart) => {
    return previewDataByChartId.value[String(chart.id)] || null;
};

const chartDataUrl = (chart) => {
    return route('forms.chart.detail', {
        id: props.initialFormId,
        chartId: chart.id,
    });
};

onMounted(async () => {
    await loadChartContext();
});
</script>

<template>
    <div>
        <div v-if="!contextLoaded" class="flex justify-center">
            <mf-spinner-medium class="w-24 h-12" />
        </div>

        <div v-else>
            <!-- Configure view: create or edit a chart -->
            <div v-if="view === 'configure'">
                <h2 class="mb-4">{{ isEditingMode ? _mft('form:chart.editChart') : _mft('form:chart.newChart') }}</h2>

                <div v-if="chartableFields.length === 0" class="text-sm text-gray-500">
                    {{ _mft('form:chart.noChartableFields') }}
                </div>

                <div v-else class="space-y-4">
                    <template v-if="isEditingMode">
                        <input-text
                            v-model:content="editDraft.name"
                            :label="_mft('form:chart.name')"
                            :placeholder="_mft('form:chart.namePlaceholder')"
                        />
                        <input-checkboxes
                            v-model:content="editDraft.field_uuids"
                            :label="_mft('form:chart.fields')"
                            name="field_uuids_edit"
                            :options="fieldOptions"
                        />
                        <input-select
                            v-model:content="editDraft.chart_type"
                            :label="_mft('form:chart.chartType')"
                            :options="chartTypeOptions"
                            :disabled-by-default="isEditingMode"
                        />
                        <div
                            v-if="supportsComparison(editDraft.chart_type) && comparisonFormOptions.length > 0"
                            class="p-4 space-y-3 border rounded-custom"
                        >
                            <toggle-switch
                                v-model:content="editDraft.comparison_settings.enabled"
                                :label="_mft('form:chart.comparison.enabled')"
                            />
                            <template v-if="editDraft.comparison_settings.enabled">
                                <input-select
                                    v-model:content="editDraft.comparison_settings.form_id"
                                    :label="_mft('form:chart.comparison.form')"
                                    :options="comparisonFormOptions"
                                />
                                <p class="text-sm text-gray-500">
                                    {{ _mft('form:chart.comparison.help') }}
                                </p>
                            </template>
                        </div>
                        <div class="flex items-center justify-end gap-2">
                            <button-secondary
                                @click="cancelConfigure"
                                :disabled="updatingChartId !== null"
                            >{{ _mft('shared:action.cancel') }}</button-secondary>
                            <button-success
                                @click="saveEditedChart"
                                :state="updatingChartId !== null ? 'waiting' : ''"
                                :disabled="!canSaveEditedChart"
                            >{{ _mft('shared:action.saveChanges') }}</button-success>
                        </div>
                    </template>

                    <template v-else>
                        <input-text
                            v-model:content="formDraft.name"
                            :required="true"
                            :label="_mft('form:chart.name')"
                            :placeholder="_mft('form:chart.namePlaceholder')"
                        />
                        <input-checkboxes
                            v-model:content="formDraft.field_uuids"
                            :label="_mft('form:chart.fields')"
                            name="field_uuids"
                            :options="fieldOptions"
                        />
                        <input-select
                            v-model:content="formDraft.chart_type"
                            :label="_mft('form:chart.chartType')"
                            :options="chartTypeOptions"
                        />
                        <div
                            v-if="supportsComparison(formDraft.chart_type) && comparisonFormOptions.length > 0"
                            class="p-4 space-y-3 border rounded-custom"
                        >
                            <toggle-switch
                                v-model:content="formDraft.comparison_settings.enabled"
                                :label="_mft('form:chart.comparison.enabled')"
                            />
                            <template v-if="formDraft.comparison_settings.enabled">
                                <input-select
                                    v-model:content="formDraft.comparison_settings.form_id"
                                    :label="_mft('form:chart.comparison.form')"
                                    :options="comparisonFormOptions"
                                />
                                <p class="text-sm text-gray-500">
                                    {{ _mft('form:chart.comparison.help') }}
                                </p>
                            </template>
                        </div>
                        <div class="flex items-center justify-end gap-2">
                            <button-secondary @click="cancelConfigure">{{ _mft('shared:action.cancel') }}</button-secondary>
                            <button-success
                                @click="createChart"
                                :state="creating ? 'waiting' : ''"
                                :disabled="!canCreateChart"
                            >{{ _mft('form:chart.createChart') }}</button-success>
                        </div>
                    </template>
                </div>
            </div>

            <!-- List view: existing charts -->
            <div v-else>
                <div v-if="charts.length === 0" class="flex items-center justify-between mb-4">
                    <h2>{{ _mft('form:chart.charts') }}</h2>
                    <button-primary
                        v-if="chartableFields.length > 0"
                        @click="startNewChart"
                    >
                        {{ _mft('form:chart.newChart') }}
                    </button-primary>
                </div>

                <p v-if="chartableFields.length === 0" class="text-sm text-gray-500">
                    {{ _mft('form:chart.noNumericFields') }}
                </p>

                <div v-else>
                    <p v-if="charts.length === 0" class="text-sm text-gray-500">
                        {{ _mft('form:chart.noChartsYet') }}
                    </p>

                    <list-basic v-else>
                        <template #header>
                            <div class="flex items-center justify-between">
                                <h2>{{ _mft('form:chart.charts') }}</h2>
                                <button-primary
                                    v-if="chartableFields.length > 0"
                                    size="sm"
                                    @click="startNewChart"
                                >
                                    {{ _mft('form:chart.newChart') }}
                                </button-primary>
                            </div>

                        </template>

                        <list-basic-item
                            v-for="chart in charts"
                            :key="chart.id"
                        >
                            <template #title>
                                <template v-if="chart.name">
                                    <a :href="chartDataUrl(chart)" class="mf-link">
                                        {{ chart.name }}
                                    </a>
                                </template>
                                <template v-else>{{ chart.fields?.map((field) => field.title || field.field_uuid).join(', ') }}</template>
                            </template>
                            <div class="text-xs text-gray-500">
                                {{ chartTypeLabel(chart.chart_type) }} · {{ chart.field_uuids?.length || 0 }} {{ _mft('form:chart.fieldCount') }}
                            </div>
                            <div v-if="chart.name" class="text-xxs text-gray-400">
                                {{ chart.fields?.map((field) => field.title || field.field_uuid).join(', ') }}
                            </div>

                            <template #actions>
                                <div class="flex items-center justify-end gap-2">
                                    <a :href="chartDataUrl(chart)">
                                        <button-secondary size="sm">
                                            <i v-if="isPreviewingChart(chart)" class="fa-regular fa-eye-slash"></i>
                                            <i v-else class="fa-regular fa-eye"></i>
                                        </button-secondary>
                                    </a>
                                    <button-secondary size="sm" @click="startEditChart(chart)">
                                        <i class="fa-regular fa-pen"></i>
                                    </button-secondary>
                                    <button-danger
                                        size="sm"
                                        @click="deleteChart(chart)"
                                        :state="deletingChartId === chart.id ? 'waiting' : ''"
                                        class="whitespace-nowrap"
                                    >
                                        <i class="fa-regular fa-trash"></i>
                                    </button-danger>
                                </div>
                            </template>
                        </list-basic-item>
                    </list-basic>
                </div>
            </div>
        </div>
    </div>
</template>
